import {
  checkinWindow,
  fixtureKickoffUtc,
  guidelines,
} from "@nrl/shared";
import { supabase } from "./supabase";
import { appNow, getAppClock } from "./appClock";

const windowConfig = {
  preKickoffMinutes: guidelines.checkinWindow.preKickoffMinutes,
  gameStandardMinutes: 100,
  postFullTimeMinutes: guidelines.checkinWindow.postFullTimeMinutes,
};

/** Map a fixtures row (including its embedded venue) to the client contract. */
export function mapFixtureRow(row) {
  if (!row?.venue) throw new Error("Fixture has no venue");
  const kickoffLocal = row.kickoff_local.slice(0, 5);
  const kickoffUtc = fixtureKickoffUtc(row.match_date, kickoffLocal, row.venue.timezone);

  // R9: an ABSENT is_origin/scoring_round/origin_game maps to the non-Origin
  // defaults (isOrigin false, scoringRound = round, originGame null); an
  // explicit null scoring_round STAYS null (never `?? row.round`).
  const isOriginVal = Boolean(row.is_origin);
  const scoringRoundVal = Object.prototype.hasOwnProperty.call(row, "scoring_round")
    ? row.scoring_round
    : row.round;
  const originGameVal = row.origin_game ?? null;

  return {
    id: row.id,
    legacyId: typeof row.legacy_id === "number" ? row.legacy_id : null,
    round: row.round,
    isOrigin: isOriginVal,
    scoringRound: scoringRoundVal,
    originGame: originGameVal,
    matchDate: row.match_date,
    kickoffLocal,
    homeRef: row.home_ref,
    awayRef: row.away_ref,
    isGrandFinal: row.is_grand_final,
    status: row.status,
    venue: {
      id: row.venue.id,
      name: row.venue.name,
      timezone: row.venue.timezone,
      lat: row.venue.lat,
      lng: row.venue.lng,
      checkinRadiusM: row.venue.checkin_radius_m,
    },
    kickoffUtc,
  };
}

export async function fetchUpcomingFixtures({ now = appNow(), limit = 5 } = {}) {
  const coarseStart = new Date(now.getTime() - 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const { data, error } = await supabase
    .from("fixtures")
    .select("id, legacy_id, round, is_origin, scoring_round, origin_game, match_date, kickoff_local, home_ref, away_ref, is_grand_final, status, venue:venues(id,name,timezone,lat,lng,checkin_radius_m)")
    .gte("match_date", coarseStart)
    .order("match_date")
    .limit(50);

  if (error) throw error;

  let skippedInvalid = false;
  const fixtures = (data ?? []).flatMap((row) => {
    try {
      return [mapFixtureRow(row)];
    } catch {
      skippedInvalid = true;
      return [];
    }
  });
  if (skippedInvalid) console.warn("[fixtures] Skipped fixtures with invalid venue timezone data");

  // R12: while a staging replay is on, RLP syncs mark replayed games 'final'
  // — those fixtures must stay visible in Log a Game, so the final exclusion
  // only applies with replay_mode off.
  const replayMode = Boolean(getAppClock()?.replay_mode);
  return fixtures
    .filter((fixture) => fixture.status !== "postponed" && (replayMode || fixture.status !== "final"))
    .filter((fixture) => {
      const { closesAt } = checkinWindow(fixture.kickoffUtc, windowConfig);
      return now.getTime() <= closesAt.getTime();
    })
    .sort((a, b) => a.kickoffUtc.getTime() - b.kickoffUtc.getTime())
    .slice(0, limit);
}

export function fixtureWindowState(fixture, now = appNow()) {
  const { opensAt, closesAt } = checkinWindow(fixture.kickoffUtc, windowConfig);
  if (now.getTime() < opensAt.getTime()) return "upcoming";
  if (now.getTime() < fixture.kickoffUtc.getTime()) return "pre";
  if (now.getTime() <= closesAt.getTime()) return "live";
  return "closed";
}

function kickoffLabel(fixture, timeZone) {
  const options = {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  };
  const label = new Intl.DateTimeFormat("en-AU", options)
    .format(fixture.kickoffUtc)
    .replace(/\s+/g, " ");
  if (!/GMT[+-]/.test(label)) return label;

  // Some ICU builds render North American short names as GMT offsets for
  // en-AU. en-US exposes the intended daylight-aware abbreviation.
  const abbreviation = new Intl.DateTimeFormat("en-US", options)
    .formatToParts(fixture.kickoffUtc)
    .find((part) => part.type === "timeZoneName")?.value;
  return abbreviation ? label.replace(/GMT[+-][\d:]+$/, abbreviation) : label;
}

export function kickoffVenueLabel(fixture) {
  return kickoffLabel(fixture, fixture.venue.timezone);
}

export function kickoffViewerLabel(fixture) {
  const viewer = kickoffLabel(fixture, undefined);
  return viewer === kickoffVenueLabel(fixture) ? null : viewer;
}

export function matchDateLabel(fixture) {
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "UTC",
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date(`${fixture.matchDate}T00:00:00Z`));
}

export async function fetchMyCheckIns(userId) {
  if (!userId) return new Set();
  const { data, error } = await supabase.from("check_ins").select("fixture_id").eq("user_id", userId);
  if (error) {
    console.warn("[fixtures] Couldn't load check-ins", error);
    return new Set();
  }
  return new Set((data ?? []).map((row) => row.fixture_id));
}
